import { API_VERSION } from '../config/api';

/**
 * Get current app version from package.json or version.json
 */
export async function getAppVersion() {
  try {
    const response = await fetch('/version.json');
    const data = await response.json();
    return data.version || '0.1.0';
  } catch (error) {
    console.warn('Unable to fetch app version, using default');
    return '0.1.0';
  }
}

/**
 * Add version headers to fetch requests
 */
export function getVersionHeaders() {
  const version = sessionStorage.getItem('app_version') || '0.1.0';

  return {
    'X-Client-Version': version,
    'X-Client-API-Version': API_VERSION,
  };
}

/**
 * Check for version compatibility warnings in response headers
 */
export function checkVersionCompatibility(response) {
  const deprecationWarning = response.headers.get('X-API-Deprecation-Warning');
  const compatibilityWarning = response.headers.get('X-API-Compatibility-Warning');
  const serverVersion = response.headers.get('X-App-Version');

  if (deprecationWarning) {
    console.warn('[API Deprecation]', deprecationWarning);
  }

  if (compatibilityWarning) {
    console.warn('[API Compatibility]', compatibilityWarning);
  }

  return {
    hasWarnings: !!(deprecationWarning || compatibilityWarning),
    deprecationWarning,
    compatibilityWarning,
    serverVersion,
  };
}

/**
 * Check if client version matches server version
 */
export async function checkVersionMismatch() {
  try {
    const clientVersion = await getAppVersion();

    // Fetch server version
    const response = await fetch('/api/version');
    const data = await response.json();

    if (data.version !== clientVersion) {
      console.warn(`Version mismatch detected. Client: ${clientVersion}, Server: ${data.version}`);
      console.warn('Consider refreshing the page to get the latest version');

      return {
        mismatch: true,
        clientVersion,
        serverVersion: data.version,
        message: 'A new version is available. Please refresh the page.',
      };
    }

    return {
      mismatch: false,
      clientVersion,
      serverVersion: data.version,
    };
  } catch (error) {
    console.error('Failed to check version compatibility:', error);
    return {
      mismatch: false,
      error: error.message,
    };
  }
}

/**
 * Initialize version checking
 * Call this when the app loads
 */
export async function initializeVersionCheck() {
  // Store app version in session storage
  const version = await getAppVersion();
  sessionStorage.setItem('app_version', version);

  // Check for version mismatch
  const versionCheck = await checkVersionMismatch();

  if (versionCheck.mismatch) {
    // Could show a notification to user here
    console.log('Version check:', versionCheck);
  }

  return versionCheck;
}
